import styles from './CreateNamedSetDialog.module.css';

import React, { useMemo, useState } from 'react';
import { Field, Formik } from 'formik';

import Dialog from 'components/ui/Dialog';
import BngField from 'components/bng/form/BngField';
import { BngCodeMirror } from 'components/bng/form/BngCodeMirror';
import BngIconButton from 'components/bng/ui/BngIconButton';
import bngYup from 'components/bng/form/yup/BngYup';
import Api from 'components/Api';
import UiMsg from 'components/ui/UiMsg';
import useTranslation from 'components/hooks/useTranslation';
import BngForm from 'components/bng/form/BngForm';

export default function CreateNamedSetDialog({
  closeModal = _.noop,
  namedSet = {},
  namedSets = [],
  inMemoryId = 0,
  onChange = _.noop,
}) {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);

  const validationSchema = useMemo(() => {
    return bngYup((yup) =>
      yup.object({
        name: yup
          .string()
          .required()
          .test({
            test(value) {
              if (_.isEmpty(value)) {
                return false;
              }
              const temp = namedSets.find((cm) => cm.name === value);
              return temp && temp.id !== namedSets?.id
                ? this.createError({ message: t('inMemory_nameAlreadyInUse') })
                : true;
            },
          })
          .default(''),
        caption: yup.string().default(''),
        formula: yup.string().required().default(''),
      })
    );
  }, [namedSets]);

  const initialValues = useMemo(() => _.merge({}, validationSchema.default(), namedSet), [namedSet]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        setLoading(true);
        try {
          values = _.merge({}, namedSet, values);
          await Api.Origin.NamedSet.save(inMemoryId, values);
          onChange();
          closeModal();
          UiMsg.ok(t('InMemoryNamedSetCreated'));
        } catch (e) {
          console.error(`Error while saving named set`, e);
          UiMsg.ajaxError(null, e);
        } finally {
          setLoading(false);
        }
      }}
    >
      {({ dirty, isSubmitting }) => (
        <Dialog
          onClose={closeModal}
          title={`${t('named.set')}: ${namedSet.caption || ''}`}
          className={`CreateNamedSetDialog ${styles.dialog}`}
          loading={isSubmitting || loading}
          newDialogLayout={true}
        >
          <BngForm>
            <Dialog.Body>
              <div className={`${styles.blockWrapper}`}>
                <Field name="name" label={t('name')} component={BngField} required={true} />
                <Field name="caption" label={t('display_name')} component={BngField} />
              </div>
              <Field
                name="formula"
                label={t('formula')}
                mode="text/x-sql"
                rootClassName={`${styles.formula}`}
                component={BngField}
                required={true}
                inputComponent={BngCodeMirror}
              />
            </Dialog.Body>

            <Dialog.Footer>
              <div className={`${styles.buttonsWrapper}`}>
                <BngIconButton
                  className={styles.saveBtn}
                  icon="save"
                  text={t('save')}
                  type="submit"
                  disabled={!dirty}
                />
              </div>
            </Dialog.Footer>
          </BngForm>
        </Dialog>
      )}
    </Formik>
  );
}
